import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconClock, IconPencil } from "@tabler/icons-react";
import type { ProductDto, ProductVariantDto } from "../../../../api";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SectionCard } from "../../../../components/common/section-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useProductVariants } from "../../hooks/useProduct";
import {
  useProductLifecycleActions,
  type ProductLifecycleAction,
} from "../../hooks/useProductLifecycleActions";
import { DisplayField } from "../components/display-field";
import {
  DetailsWarrantySection,
  type WarrantyDisplayRow,
  type NewWarrantyInput,
} from "../components/details-warranty-section";
import {
  DetailsVariantsSection,
  type VariantDisplayRow,
  type PendingNewVariantRow,
} from "../components/details-variants-section";
import { ProductNotFound } from "../components/product-not-found";
import { BuyerPricingPanel } from "../components/buyer-pricing-panel";
import { CompatibleWarrantiesSection } from "../components/compatible-warranties-section";
import { resolveImageSource } from "../../../../components/common/image";
import { ImageDropzone } from "../components/image-dropzone";
import { getProductLifecycleStatus } from "../components/product-lifecycle.utils";
import { ProductLifecycleChip } from "../components/product-lifecycle";
import { ProductStatusDialog } from "../components/product-status-dialog";
import { Chip } from "../../../../components/common/chip";
import { useMyPendingProductChangeRequests } from "../../../change-requests/hooks/useMyPendingProductChangeRequests";
import { useProductAttributeLines } from "../../hooks/useProductAttributeLines";
import { useCountries } from "../../hooks/useCountries";
import type { ChangeRequestDto } from "../../../../api";

function deriveProductImages(product: ProductDto, variants: ProductVariantDto[]): string[] {
  const gallery = product.galleryImages ?? [];
  const images = [
    product.image,
    ...gallery,
    ...variants.map((v) => v.image),
  ].filter((img): img is string => Boolean(img));
  return Array.from(new Set(images));
}

function deriveTemplateWarranty(product: ProductDto): WarrantyDisplayRow[] {
  // Backend currently exposes a single warranty + warranty type per template;
  // there is no list endpoint for multiple warranty options. The "Add warranty"
  // UX below is therefore client-only until the backend exposes one.
  if (!product.warranty?.name && !product.warrantyType?.name) return [];
  return [
    {
      id: 1,
      period: product.warranty?.name ?? "—",
      type: product.warrantyType?.name ?? "—",
      provider: "Merchant warranty",
    },
  ];
}

function variantToRow(v: ProductVariantDto): VariantDisplayRow {
  return {
    id: v.id,
    variant: v.displayName ?? v.name ?? `Variant #${v.id}`,
    sku: v.defaultCode ?? "—",
    price: v.sellerPrice ?? (v.listPrice ?? 0) + (v.priceExtra ?? 0),
    stock: v.supplierStock ?? 0,
    discount: v.discountPercentage ?? 0,
    imageUrl: v.image ?? undefined,
  };
}

export function MyProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productId = Number(id ?? 0);
  const { updateLifecycle, isSaving } = useProductLifecycleActions();

  const { data, isLoading, error } = useProductVariants(productId);
  const { data: pendingCrs } = useMyPendingProductChangeRequests();
  const { data: attributeLines } = useProductAttributeLines(productId);
  const { data: countries } = useCountries();
  const pendingCr = useMemo<ChangeRequestDto | null>(
    () => pendingCrs?.pending.find((r) => r.targetProductId === productId) ?? null,
    [pendingCrs?.pending, productId],
  );
  const hasPendingReview = pendingCr !== null;
  const [pendingLifecycle, setPendingLifecycle] = useState<{
    product: ProductDto;
    action: ProductLifecycleAction;
  } | null>(null);

  const initialVariants = useMemo<VariantDisplayRow[]>(
    () => (data?.variants ?? []).map(variantToRow),
    [data?.variants],
  );
  const initialWarranties = useMemo<WarrantyDisplayRow[]>(
    () => (data?.product ? deriveTemplateWarranty(data.product) : []),
    [data?.product],
  );

  const [variants] = useState<VariantDisplayRow[] | null>(null);
  const [warranties, setWarranties] = useState<WarrantyDisplayRow[] | null>(null);
  const [newWarranty, setNewWarranty] = useState<NewWarrantyInput | null>(null);

  // Read-only on the details page; the editor lives on /my-products/:id/edit.
  const compatibleWarrantyIds: number[] = data?.product?.compatibleWarrantyIds ?? [];

  const displayVariants = variants ?? initialVariants;
  const displayWarranties = warranties ?? initialWarranties;

  function handleSaveWarranty() {
    if (!newWarranty) return;
    // No backend endpoint to persist additional warranties — kept client-side
    // until BE exposes a multi-warranty resource.
    setWarranties([...displayWarranties, { id: Date.now(), ...newWarranty }]);
    setNewWarranty(null);
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
        <ShimmerLoader message="Fetching product details…" lines={6} />
      </div>
    );
  }

  if (error || !data?.product) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
        <ProductNotFound error={error} />
      </div>
    );
  }

  const product = data.product;
  const productName = product.displayName ?? product.name ?? "Product";
  const lifecycleStatus = getProductLifecycleStatus(product);
  const images = deriveProductImages(product, data.variants ?? []);
  const resolvedImages = images.map((src) => resolveImageSource(src)).filter((src): src is string => Boolean(src));

  // Resolve the pending CR's proposedChanges into per-field display strings
  // and per-variant overlays so each row/cell can show its own "Pending: X"
  // annotation, mirroring the settings page pattern.
  const proposed = (pendingCr?.proposedChanges as Record<string, unknown> | undefined) ?? {};
  const proposedDescription =
    typeof proposed.description === "string" ? proposed.description : null;
  const proposedName = typeof proposed.name === "string" ? proposed.name : null;
  const proposedCountryName =
    typeof proposed.countryOfOriginId === "number"
      ? countries?.find((c) => c.id === proposed.countryOfOriginId)?.name ?? `#${proposed.countryOfOriginId}`
      : null;
  const proposedHasImage = typeof proposed.image === "string" && proposed.image.length > 0;
  const proposedGallery = (proposed.gallery as { keepIds?: number[]; add?: string[] } | undefined) ?? null;
  const proposedHasGallery = !!proposedGallery && (
    (proposedGallery.add?.length ?? 0) > 0 ||
    (proposedGallery.keepIds ? proposedGallery.keepIds.length !== (product.galleryImageIds?.length ?? 0) : false)
  );
  const proposedVariants = Array.isArray(proposed.variants)
    ? (proposed.variants as Array<{ id: number; quantity?: number; sellerPrice?: number; image?: string }>)
    : [];
  const proposedDiscounts = Array.isArray(proposed.discounts)
    ? (proposed.discounts as Array<{ variantId: number; discountPercentage: number }>)
    : [];
  const proposedArchive = Array.isArray(proposed.archiveVariantIds)
    ? (proposed.archiveVariantIds as number[])
    : [];
  const proposedNewVariants = Array.isArray(proposed.newVariants)
    ? (proposed.newVariants as Array<{
        attributeValues?: Array<{ attributeId: number; valueId?: number; newValueName?: string }>;
        sku?: string;
        sellerPrice?: number;
        quantity?: number;
        image?: string;
        discountPercentage?: number;
      }>)
    : [];

  // Resolve attribute value IDs → names so a pending new variant reads as
  // "1 TB / Black" instead of raw "#42 / #57".
  const attributeValueNameById = new Map<number, string>();
  for (const line of attributeLines ?? []) {
    for (const val of line.values) attributeValueNameById.set(val.id, val.name);
  }
  const pendingNewVariantRows: PendingNewVariantRow[] = proposedNewVariants.map((nv) => ({
    name:
      (nv.attributeValues ?? [])
        .map((av) =>
          av.newValueName?.trim()
            ? `${av.newValueName.trim()} (new)`
            : av.valueId != null
              ? attributeValueNameById.get(av.valueId) ?? `#${av.valueId}`
              : null,
        )
        .filter((s): s is string => s != null)
        .join(" / ") || "New variant",
    sku: nv.sku,
    price: nv.sellerPrice,
    stock: nv.quantity,
    discount: nv.discountPercentage,
    hasImage: typeof nv.image === "string" && nv.image.length > 0,
  }));

  const pendingVariantOverlay = new Map<
    number,
    { price?: number; stock?: number; image?: boolean; discount?: number; archive?: boolean }
  >();
  for (const v of proposedVariants) {
    const entry = pendingVariantOverlay.get(v.id) ?? {};
    if (typeof v.sellerPrice === "number") entry.price = v.sellerPrice;
    if (typeof v.quantity === "number") entry.stock = v.quantity;
    if (typeof v.image === "string" && v.image.length > 0) entry.image = true;
    pendingVariantOverlay.set(v.id, entry);
  }
  for (const d of proposedDiscounts) {
    const entry = pendingVariantOverlay.get(d.variantId) ?? {};
    entry.discount = d.discountPercentage;
    pendingVariantOverlay.set(d.variantId, entry);
  }
  for (const id of proposedArchive) {
    const entry = pendingVariantOverlay.get(id) ?? {};
    entry.archive = true;
    pendingVariantOverlay.set(id, entry);
  }

  const pendingCount =
    (proposedName != null ? 1 : 0) +
    (proposedDescription != null ? 1 : 0) +
    (proposedCountryName != null ? 1 : 0) +
    (proposedHasImage ? 1 : 0) +
    (proposedHasGallery ? 1 : 0) +
    pendingVariantOverlay.size +
    proposedNewVariants.length;
  const submittedAtLabel = pendingCr?.submittedAt
    ? new Date(pendingCr.submittedAt).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">

      <div className="flex items-center gap-2">
        <Button variant="ghostAccent" size="sm" leftIcon={<IconArrowLeft size={18} />}
          onClick={() => navigate(AppRoutes.MY_PRODUCTS)} className="!h-auto !px-0 !py-0">
          <Heading variant="h3" tone="inherit">Back to Products</Heading>
        </Button>
        {/* Phones: the title right below already names the product. */}
        <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
        <Heading variant="h3" tone="dark" className="max-md:hidden">Product Detail - {productName}</Heading>
      </div>

      <div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-4">
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <Heading variant="h2" tone="dark">{productName}</Heading>
          <ProductLifecycleChip product={product} />
          {hasPendingReview && <Chip variant="inReview">Pending review</Chip>}
        </div>
        <div className="flex items-center gap-3 max-md:grid max-md:grid-cols-2">
          {lifecycleStatus === "live" ? (
            <>
              <Button
                variant="secondaryDark"
                size="md"
                className="!px-6"
                onClick={() => setPendingLifecycle({ product, action: "inactive" })}
              >
                Set Inactive
              </Button>
              <Button
                variant="secondaryDanger"
                size="md"
                className="!px-6"
                onClick={() => setPendingLifecycle({ product, action: "discontinued" })}
              >
                Discontinue
              </Button>
            </>
          ) : (
            <Button
              variant="secondaryDark"
              size="md"
              className="!px-6"
              onClick={() => setPendingLifecycle({ product, action: "reactivate" })}
            >
              Reactivate
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            className="!px-6"
            onClick={() =>
              window.open(
                `${AppRoutes.PRODUCT_DETAILS.replace(":id", String(product.id))}?preview=1`,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            Preview
          </Button>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<IconPencil size={16} />}
            className="!px-6"
            disabled={hasPendingReview}
            title={hasPendingReview ? "A change request is already pending admin review." : undefined}
            onClick={() => navigate(AppRoutes.MY_PRODUCT_EDIT.replace(":id", String(product.id)))}
          >
            Edit
          </Button>
        </div>
      </div>

      {pendingCr && (
        <div className="flex items-start gap-3 rounded-2xl border border-accentAmber bg-accentYellowTint px-4 py-3">
          <IconClock size={18} className="mt-0.5 shrink-0 text-accentAmber" />
          <div className="flex flex-1 flex-col gap-0.5">
            <Text variant="bodySmBold" tone="amber">
              {pendingCount} change{pendingCount === 1 ? "" : "s"} waiting for admin approval
            </Text>
            <Text variant="bodyXs" tone="amber">
              {submittedAtLabel ? `Submitted ${submittedAtLabel}. ` : ""}
              The values below still show what's currently live; pending values are noted under each field.
            </Text>
          </div>
          <button
            type="button"
            onClick={() => navigate(AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", pendingCr.id))}
            className="shrink-0 self-start"
          >
            <Text variant="bodySmBold" tone="amber" inline className="underline">
              View request
            </Text>
          </button>
        </div>
      )}

      <SectionCard shadow title="General Product images" className="gap-5">
        <ImageDropzone
          images={resolvedImages}
          onImagesChange={() => {}}
          readOnly
        />
        {(proposedHasImage || proposedHasGallery) && (
          <div className="flex items-center gap-1.5 px-1.5">
            <IconClock size={14} className="shrink-0 text-accentAmber" />
            <Text variant="bodyXs" tone="amber" inline>
              Pending: {proposedHasImage ? "main image" : ""}
              {proposedHasImage && proposedHasGallery ? " and " : ""}
              {proposedHasGallery ? "gallery changes" : ""} — awaiting approval
            </Text>
          </div>
        )}
      </SectionCard>

      <SectionCard shadow title="Basic information" className="gap-5">
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <DisplayField
            label="Product name"
            value={productName}
            pending={proposedName}
          />
          <DisplayField label="Category"          value={product.category?.name ?? "—"} />
          <DisplayField label="SKU"               value={product.defaultCode ?? "—"} />
          <DisplayField
            label="Country of origin"
            value={product.countryOfOrigin?.name ?? "—"}
            pending={proposedCountryName}
          />
          <DisplayField
            label="Description"
            value={product.description ?? "No description provided."}
            span2
            pending={proposedDescription}
          />
        </div>
      </SectionCard>

      <DetailsWarrantySection
        warranties={displayWarranties}
        newWarranty={newWarranty}
        onWarrantyRemove={(wid) => setWarranties(displayWarranties.filter((x) => x.id !== wid))}
        onNewWarrantyChange={(patch) => setNewWarranty((prev) => prev ? { ...prev, ...patch } : prev)}
        onNewWarrantyStart={() => setNewWarranty({ period: "", type: "", provider: "" })}
        onNewWarrantySave={handleSaveWarranty}
        onNewWarrantyCancel={() => setNewWarranty(null)}
        readOnly
      />

      <CompatibleWarrantiesSection
        selectedIds={compatibleWarrantyIds}
        onChange={() => {}}
        readOnly
      />

      {/* Recent orders: backend has no per-product orders endpoint yet. Hidden
          until /products/:id/orders (or equivalent) ships. */}

      <DetailsVariantsSection
        variants={displayVariants}
        pendingOverlay={pendingVariantOverlay}
        pendingNewVariants={pendingNewVariantRows}
      />

      {product.id && (data?.variants ?? []).length > 0 && (
        <BuyerPricingPanel
          templateId={product.id}
          variants={data?.variants ?? []}
        />
      )}

      <ProductStatusDialog
        open={pendingLifecycle !== null}
        action={pendingLifecycle?.action ?? null}
        productName={productName}
        isSaving={isSaving}
        onConfirm={async () => {
          if (!pendingLifecycle) return;
          try {
            await updateLifecycle(pendingLifecycle.product.id, pendingLifecycle.action);
            setPendingLifecycle(null);
          } catch {
            // Toast already shown by the hook; keep the dialog open for retry.
          }
        }}
        onClose={() => setPendingLifecycle(null)}
      />

    </div>
  );
}
